import { useRef } from 'react';

import { push } from 'connected-react-router';
import { Form, withFormik } from 'formik';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Typography } from 'src/app/components/Elements/Typography';
import TextInput from 'src/app/components/Form/TextInput';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { AccountPagePhoneUpdateSuccess } from 'src/app/router/paths';
import { patchAccount, segmentTrack } from 'src/app/state/actions';
import type { ExtendedReducerState } from 'src/app/state/reducers';
import { getCurrentAccountId } from 'src/app/state/selectors';
import type { Dispatch } from 'src/app/state/store';

import { ButtonContainer } from '../../GetStarted/styles';
import { formatPhoneNumber } from '../../GetStarted/util';

import { ContentContainer, ContinueButton, OuterContainer } from './styles';
import { validation } from './validation';

const UpdatePhone = ({ isSubmitting, isValid }) => {
  const formRef = useRef(null);

  const phoneNumberMask = '999-999-9999';

  return (
    <OuterContainer>
      <ContentContainer>
        <Typography variant="h1" marginBottom={'s2'}>
          Update your mobile number
        </Typography>

        <Typography variant="body" marginBottom="s5">
          Enter the best mobile number to reach you. We'll need this to verify your account via text message the next
          time you log in.
        </Typography>

        <Form ref={formRef}>
          <TextInput
            name="phone_number"
            type="tel"
            label="MOBILE NUMBER"
            autoComplete="none"
            alwaysShowMask={false}
            maskChar={null}
            mask={phoneNumberMask}
          />

          <ButtonContainer smallMarginTop>
            <ContinueButton size="medium" q style="capsuleBlue50" disabled={!isValid || isSubmitting}>
              Continue
            </ContinueButton>
          </ButtonContainer>
        </Form>
      </ContentContainer>
    </OuterContainer>
  );
};

const mapStateToProps = () => {
  return (state: ExtendedReducerState) => {
    const accountId = getCurrentAccountId(state);
    return {
      accountId,
    };
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    dispatchUpdatePhone: async (account_id, phone) => {
      await dispatch(patchAccount(account_id, { phone }));
    },
    dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
    dispatchSuccessPage: () => dispatch(push(AccountPagePhoneUpdateSuccess.toPath())),
  };
};

const enhancedWithFormik = withFormik({
  mapPropsToValues: validation.initialValues,
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: false,
  validateOnMount: true,
  handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
    const { dispatchUpdatePhone, accountId, dispatchSuccessPage, dispatchSegmentTrack } = props;

    setSubmitting(true);
    dispatchSegmentTrack(UNIFIED.submitUpdatedPhoneNumber);
    try {
      await dispatchUpdatePhone(accountId, formatPhoneNumber(values.phone_number));
      dispatchSegmentTrack(UNIFIED.submitUpdatedPhoneNumberSuccess);
      dispatchSuccessPage();
    } catch (error) {
      const bffError = error?.error;
      const errorCode = bffError?.errorCode;
      const errorSummary = bffError?.flavorText;

      const errors = extractValidationErrors(error);

      dispatchSegmentTrack(UNIFIED.submitUpdatedPhoneNumberFailed, {
        errorCode: errorCode ? errorCode : '400',
        errorSummary: errorSummary ? errorSummary : errors._generic,
      });
      setErrors({ phone_number: 'Error updating phone number. Please try again.' });
    }
    setSubmitting(false);
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

export const AccountPageUpdatePhone = compose(connector, enhancedWithFormik)(UpdatePhone);
